import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemSecondaryAction,
    IconButton,
    TextField,
    Chip,
    LinearProgress,
    Alert,
    Paper,
    FormControlLabel,
    Switch
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    InsertDriveFile as FileIcon,
    Image as ImageIcon,
    VideoFile as VideoIcon,
    AudioFile as AudioIcon,
    PictureAsPdf as PdfIcon,
    TextFields as TextIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';

const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <ImageIcon />;
    if (type.startsWith('video/')) return <VideoIcon />;
    if (type.startsWith('audio/')) return <AudioIcon />;
    if (type === 'application/pdf') return <PdfIcon />;
    if (type.startsWith('text/')) return <TextIcon />;
    return <FileIcon />;
};

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const StimuliUploader = ({ open, onClose, onStimuliSetCreated, experimentId = null }) => {
    const [files, setFiles] = useState([]);
    const [stimuliSetName, setStimuliSetName] = useState('');
    const [stimuliSetDescription, setStimuliSetDescription] = useState('');
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [processImages, setProcessImages] = useState(true);
    const [error, setError] = useState('');
    
    const { getAccessTokenSilently } = useAuth0();

    const onDrop = useCallback((acceptedFiles) => {
        const newFiles = acceptedFiles.map(file => ({
            file,
            id: Math.random().toString(36).substr(2, 9),
            title: file.name.split('.')[0],
            description: ''
        }));
        setFiles(prev => [...prev, ...newFiles]);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.gif'],
            'video/*': ['.mp4', '.webm', '.ogg', '.avi'],
            'audio/*': ['.mp3', '.wav', '.ogg', '.m4a'],
            'application/pdf': ['.pdf'],
            'text/*': ['.txt', '.md', '.html']
        },
        maxSize: 100 * 1024 * 1024, // 100MB
        multiple: true
    });

    const removeFile = (fileId) => {
        setFiles(files.filter(f => f.id !== fileId));
    };

    const updateFileDetails = (fileId, field, value) => {
        setFiles(files.map(f => 
            f.id === fileId ? { ...f, [field]: value } : f
        ));
    };

    const addTag = () => {
        if (tagInput.trim() && !tags.includes(tagInput.trim())) {
            setTags([...tags, tagInput.trim()]);
            setTagInput('');
        }
    };

    const removeTag = (tagToRemove) => {
        setTags(tags.filter(tag => tag !== tagToRemove));
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addTag();
        }
    };

    const handleUpload = async () => {
        if (!stimuliSetName.trim()) {
            setError('Please provide a name for the stimuli set');
            return;
        }

        if (files.length === 0) {
            setError('Please select at least one file');
            return;
        }

        setUploading(true);
        setError('');
        setUploadProgress(0);

        try {
            const token = await getAccessTokenSilently();
            
            // Prepare form data
            const formData = new FormData();
            files.forEach(fileData => {
                formData.append('files', fileData.file);
            });
            
            formData.append('experimentId', experimentId || '');
            formData.append('tags', JSON.stringify(tags));
            formData.append('processImages', processImages.toString());

            // Upload files first
            const uploadResponse = await axios.post(
                `${API_URL}/api/stimuli/upload`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round(
                            (progressEvent.loaded * 50) / progressEvent.total
                        );
                        setUploadProgress(progress);
                    }
                }
            );

            if (uploadResponse.data.errors && uploadResponse.data.errors.length > 0) {
                console.warn('Some files failed to upload:', uploadResponse.data.errors);
            }

            const uploadedFiles = uploadResponse.data.uploaded;
            
            // Create stimuli set
            setUploadProgress(75);
            const stimuliSetData = {
                name: stimuliSetName,
                description: stimuliSetDescription,
                experimentId: experimentId,
                files: uploadedFiles.map((uploadedFile, index) => {
                    const originalFile = files[index];
                    return {
                        fileId: uploadedFile.fileId,
                        order: index,
                        title: originalFile.title,
                        description: originalFile.description,
                        settings: {}
                    };
                }),
                questions: [],
                settings: {
                    randomizeOrder: false,
                    showProgress: true,
                    allowSkip: false,
                    minViewTime: 0,
                    autoAdvance: false,
                    autoAdvanceTime: 0
                }
            };

            const setResponse = await axios.post(
                `${API_URL}/api/stimuli/sets`,
                stimuliSetData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            setUploadProgress(100);

            // Get the created set with file details
            const createdSetResponse = await axios.get(
                `${API_URL}/api/stimuli/sets/${setResponse.data.stimuliSetId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            // Call the callback with the created set
            onStimuliSetCreated(createdSetResponse.data);
            
            // Reset form
            setFiles([]);
            setStimuliSetName('');
            setStimuliSetDescription('');
            setTags([]);
            setUploadProgress(0);
            
            onClose();
        } catch (err) {
            console.error('Error uploading stimuli:', err);
            setError(err.response?.data?.message || 'Failed to upload stimuli');
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        if (!uploading) {
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Upload Stimuli Files</DialogTitle>
            
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Set Details */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        Stimuli Set Details
                    </Typography>
                    
                    <TextField
                        label="Set Name"
                        value={stimuliSetName}
                        onChange={(e) => setStimuliSetName(e.target.value)}
                        fullWidth
                        required
                        sx={{ mb: 2 }}
                    />
                    
                    <TextField
                        label="Description"
                        value={stimuliSetDescription}
                        onChange={(e) => setStimuliSetDescription(e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        sx={{ mb: 2 }}
                    />

                    {/* Tags */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Tags</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            {tags.map((tag, index) => (
                                <Chip
                                    key={index}
                                    label={tag}
                                    onDelete={() => removeTag(tag)}
                                    size="small"
                                />
                            ))}
                        </Box>
                        <TextField
                            label="Add tag"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            size="small"
                            sx={{ mr: 1 }}
                        />
                        <Button onClick={addTag} size="small">Add</Button>
                    </Box>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={processImages}
                                onChange={(e) => setProcessImages(e.target.checked)}
                            />
                        }
                        label="Process images (resize and optimize)"
                    />
                </Box>

                {/* File Upload Area */}
                <Paper
                    {...getRootProps()}
                    sx={{
                        border: '2px dashed',
                        borderColor: isDragActive ? 'primary.main' : 'grey.300',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        mb: 2,
                        bgcolor: isDragActive ? 'action.hover' : 'background.paper'
                    }}
                >
                    <input {...getInputProps()} />
                    <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        {isDragActive ? 'Drop files here' : 'Drag & drop files here, or click to browse'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Supports: Images (JPEG, PNG, WebP, GIF), Videos (MP4, WebM, OGG, AVI), 
                        Audio (MP3, WAV, OGG, M4A), PDFs, and Text files
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                        Maximum file size: 100MB
                    </Typography>
                </Paper>

                {/* File List */}
                {files.length > 0 && (
                    <Box>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Selected Files ({files.length})
                        </Typography>
                        
                        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                            {files.map((fileData) => (
                                <ListItem key={fileData.id} divider>
                                    <ListItemIcon>
                                        {getFileIcon(fileData.file.type)}
                                    </ListItemIcon>
                                    
                                    <ListItemText
                                        primary={
                                            <Box>
                                                <Typography variant="body1">
                                                    {fileData.file.name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {formatFileSize(fileData.file.size)} • {fileData.file.type}
                                                </Typography>
                                            </Box>
                                        }
                                        secondary={
                                            <Box sx={{ mt: 1 }}>
                                                <TextField
                                                    label="Title"
                                                    value={fileData.title}
                                                    onChange={(e) => updateFileDetails(fileData.id, 'title', e.target.value)}
                                                    size="small"
                                                    sx={{ mr: 1, width: '200px' }}
                                                />
                                                <TextField
                                                    label="Description"
                                                    value={fileData.description}
                                                    onChange={(e) => updateFileDetails(fileData.id, 'description', e.target.value)}
                                                    size="small"
                                                    sx={{ width: '250px' }}
                                                />
                                            </Box>
                                        }
                                    />
                                    
                                    <ListItemSecondaryAction>
                                        <IconButton 
                                            edge="end" 
                                            onClick={() => removeFile(fileData.id)}
                                            disabled={uploading}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {/* Upload Progress */}
                {uploading && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Uploading... {uploadProgress}%
                        </Typography>
                        <LinearProgress variant="determinate" value={uploadProgress} />
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={uploading}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleUpload} 
                    variant="contained"
                    disabled={uploading || files.length === 0 || !stimuliSetName.trim()}
                >
                    {uploading ? 'Uploading...' : 'Create Stimuli Set'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default StimuliUploader;
